import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Building2, Edit3, GitMerge, AlertTriangle, Search,
  RefreshCw, ShieldCheck, Download, Mail, FileBarChart, CheckSquare, X,
  ChevronDown, ChevronUp, Filter, Plus, MoreVertical, Eye, FileText,
  ChevronLeft, ChevronRight, Users as UsersIcon,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import ExternalReferenceConfigCard from '../../components/admin/ExternalReferenceConfigCard';
import ClientStatementDialog from '../../components/admin/ClientStatementDialog';
import {
  ClientCell, CompletenessBlock, FiscalityCell, PaymentCell, ActivityCell, RelationCell,
} from '../../components/admin/companies/CompanyRowCells';
import { AssignCommercialDialog } from '../../components/admin/companies/AssignCommercialDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TVA_OPTIONS = [
  { value: '', label: '—' },
  { value: 'assujetti', label: 'Assujetti TVA (18%)' },
  { value: 'exo_legale', label: 'Exonéré légal' },
  { value: 'exo_conv', label: 'Exonéré conventionnel' },
  { value: 'non_assujetti', label: 'Non assujetti (TVAD 0%)' },
];

const SORT_FIELDS = {
  companyName: (a, b) => (a.companyName || '').localeCompare(b.companyName || ''),
  members: (a, b) => (a.memberCount || 0) - (b.memberCount || 0),
  operations: (a, b) => (a.operationsCount || 0) - (b.operationsCount || 0),
  fne: (a, b) => Number(a.fneBillingEnabled) - Number(b.fneBillingEnabled),
};

export default function CompaniesAdminPage() {
  const [companies, setCompanies] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filterFne, setFilterFne] = useState('all');
  const [filterTva, setFilterTva] = useState('all');
  const [filterSector, setFilterSector] = useState('all');
  const [filterReadiness, setFilterReadiness] = useState(searchParams.get('readiness') || 'all');
  const [sortBy, setSortBy] = useState('companyName');
  const [sortDir, setSortDir] = useState('asc');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Dialogs
  const [profileDrawer, setProfileDrawer] = useState(null); // company being edited
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [auditItems, setAuditItems] = useState([]);

  const [renameDialog, setRenameDialog] = useState(null);
  const [renameForm, setRenameForm] = useState({ newName: '', retroactive: false, reason: '' });
  const [similar, setSimilar] = useState([]);

  const [mergeDialog, setMergeDialog] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');

  const [bulkPolicyDialog, setBulkPolicyDialog] = useState(false);
  const [bulkSectorDialog, setBulkSectorDialog] = useState(false);
  const [bulkPolicyValue, setBulkPolicyValue] = useState('');
  const [bulkSectorValue, setBulkSectorValue] = useState('');

  const [reportDialog, setReportDialog] = useState(null);
  const [reportPeriod, setReportPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const [statementDialog, setStatementDialog] = useState(false);
  const [statementClient, setStatementClient] = useState(null);

  // New company info dialog
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);

  // Sprint 1 — Assign commercial dialog
  const [assignCommercialOpen, setAssignCommercialOpen] = useState(false);
  const [assignCommercialClient, setAssignCommercialClient] = useState(null);

  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/companies`);
      setCompanies(r.data.items || []);
    } catch (e) {
      toast.error('Erreur', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const loadAux = async () => {
      try {
        const [pol, sec] = await Promise.all([
          axios.get(`${API}/payment-policies/`),
          axios.get(`${API}/admin/business-sectors`),
        ]);
        setPolicies(pol.data || []);
        setSectors(sec.data || []);
      } catch { /* ignore */ }
    };
    loadAux();
  }, [load]);

  // Derived sector & TVA options
  const sectorOptions = useMemo(() => {
    const codes = new Set(companies.filter((c) => c.sectorCode).map((c) => c.sectorCode));
    return Array.from(codes).map((code) => ({
      code,
      name: companies.find((c) => c.sectorCode === code)?.sectorName || code,
    }));
  }, [companies]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = [...companies];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.companyName || '').toLowerCase().includes(q) ||
          (c.ncc || '').toLowerCase().includes(q) ||
          (c.rccm || '').toLowerCase().includes(q) ||
          (c.billingEmailEffective || '').toLowerCase().includes(q)
      );
    }
    if (filterFne !== 'all') {
      const want = filterFne === 'yes';
      list = list.filter((c) => Boolean(c.fneBillingEnabled) === want);
    }
    if (filterTva !== 'all') list = list.filter((c) => c.tvaStatus === filterTva);
    if (filterSector !== 'all') list = list.filter((c) => c.sectorCode === filterSector);
    if (filterReadiness !== 'all') {
      // Critères critiques pour FNE PROD : NCC + RCCM + TVA + email facturation (ou fallback) + Secteur
      const isReady = (c) => Boolean(c.ncc) && Boolean(c.rccm) && Boolean(c.tvaStatus)
        && Boolean(c.billingEmail || c.billingEmailFallback) && Boolean(c.sectorCode);
      if (filterReadiness === 'ready') list = list.filter(isReady);
      if (filterReadiness === 'incomplete') list = list.filter((c) => !isReady(c));
    }
    const cmp = SORT_FIELDS[sortBy] || SORT_FIELDS.companyName;
    list.sort((a, b) => (sortDir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return list;
  }, [companies, search, filterFne, filterTva, filterSector, sortBy, sortDir]);

  // Reset to page 1 whenever filtered list changes — clamp in derived value
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const effectivePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (effectivePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, effectivePage, pageSize]);

  // When filters/search change → user clicks reset means we set page=1
  const resetToFirstPage = useCallback(() => setPage(1), []);

  const allSelected = paginated.length > 0 && paginated.every((c) => selected.has(c.companyName));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      paginated.forEach((c) => next.delete(c.companyName));
      setSelected(next);
    } else {
      const next = new Set(selected);
      paginated.forEach((c) => next.add(c.companyName));
      setSelected(next);
    }
  };
  const toggleOne = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const handleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // === Profile drawer ===
  const openProfileDrawer = async (company) => {
    setProfileDrawer(company);
    setProfileForm({
      rccm: company.rccm || '',
      compteContribuable: company.ncc || '',
      address: company.address || '',
      tvaStatus: company.tvaStatus || '',
      billingEmail: company.billingEmail || '',
      sectorCode: company.sectorCode || '',
      customSector: company.customSector || '',
      defaultPaymentPolicyId: company.defaultPaymentPolicyId || '',
      preferredPaymentMode: company.preferredPaymentMode || '',
      fneBillingEnabled: !!company.fneBillingEnabled,
      fneBillingStartDate: company.fneBillingStartDate || '',
    });
    setAuditItems([]);
    try {
      const r = await axios.get(`${API}/admin/companies/${encodeURIComponent(company.companyName)}/audit`);
      setAuditItems(r.data.items || []);
    } catch { /* ignore audit fetch errors */ }
  };

  const submitProfile = async () => {
    if (!profileDrawer) return;
    setSavingProfile(true);
    try {
      const payload = { ...profileForm };
      // Strip empty strings to send null/undefined where appropriate
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });
      const r = await axios.patch(
        `${API}/admin/companies/${encodeURIComponent(profileDrawer.companyName)}/profile`,
        payload
      );
      toast.success('Profil entreprise mis à jour', {
        description: `${r.data.membersAffected} membre(s) impacté(s) — ${r.data.fieldsChanged?.length || 0} champ(s)`,
      });
      setProfileDrawer(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error('Erreur', { description: typeof d === 'string' ? d : e.message });
    } finally {
      setSavingProfile(false);
    }
  };

  // === Rename ===
  const openRename = (c) => {
    setRenameDialog(c);
    setRenameForm({ newName: c.companyName, retroactive: false, reason: '' });
    setSimilar([]);
  };
  const checkSimilar = async (name) => {
    if (!name || name.length < 3) return setSimilar([]);
    try {
      const r = await axios.get(`${API}/admin/companies/similar`, { params: { name, threshold: 0.75 } });
      setSimilar((r.data.matches || []).filter((m) => m.companyName !== renameDialog?.companyName));
    } catch {
      setSimilar([]);
    }
  };
  const submitRename = async () => {
    if (!renameDialog) return;
    try {
      const r = await axios.post(`${API}/admin/companies/rename`, {
        oldName: renameDialog.companyName,
        newName: renameForm.newName,
        retroactive: renameForm.retroactive,
        reason: renameForm.reason,
      });
      toast.success('Entreprise renommée', {
        description: `${r.data.usersUpdated} user(s), ${r.data.opsUpdated} op(s), ${r.data.loadingNotesUpdated} BL(s)`,
      });
      setRenameDialog(null);
      load();
    } catch (e) {
      toast.error('Erreur rename', { description: e?.response?.data?.detail || e.message });
    }
  };

  // === Merge ===
  const submitMerge = async () => {
    if (!mergeDialog || !mergeTarget) return;
    try {
      const r = await axios.post(`${API}/admin/companies/merge`, {
        sourceName: mergeDialog.companyName,
        targetName: mergeTarget,
        retroactive: true,
      });
      toast.success('Fusion réussie', {
        description: `${r.data.usersMoved} user(s), ${r.data.opsMoved} op(s) → ${mergeTarget}`,
      });
      setMergeDialog(null);
      setMergeTarget('');
      load();
    } catch (e) {
      toast.error('Erreur fusion', { description: e?.response?.data?.detail || e.message });
    }
  };

  // === Bulk actions ===
  const runBulk = async (action, payload = {}) => {
    if (selected.size === 0) return;
    try {
      const r = await axios.post(`${API}/admin/companies/bulk-action`, {
        companies: Array.from(selected),
        action,
        payload,
      });
      toast.success(`Action "${action}" appliquée`, {
        description: `${r.data.companiesCount} entreprise(s) · ${r.data.usersAffected} user(s) mis à jour`,
      });
      clearSelection();
      setBulkPolicyDialog(false);
      setBulkSectorDialog(false);
      load();
    } catch (e) {
      toast.error('Erreur', { description: e?.response?.data?.detail || e.message });
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.post(
        `${API}/admin/companies/export-csv`,
        { companies: selected.size > 0 ? Array.from(selected) : [] },
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entreprises_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error('Erreur export', { description: e?.response?.data?.detail || e.message });
    }
  };

  // === Statement & Report ===
  const openStatement = async (company) => {
    try {
      const r = await axios.get(`${API}/admin/companies/${encodeURIComponent(company.companyName)}/statement-primary-client`);
      const c = r.data.primaryClient;
      setStatementClient({
        id: c.id,
        email: c.email,
        firstName: c.firstName,
        lastName: c.lastName,
        companyName: company.companyName,
      });
      setStatementDialog(true);
    } catch (e) {
      toast.error('Impossible de générer le relevé', { description: e?.response?.data?.detail || e.message });
    }
  };

  const downloadReport = async () => {
    if (!reportDialog) return;
    try {
      const r = await axios.get(
        `${API}/admin/companies/${encodeURIComponent(reportDialog.companyName)}/monthly-report-pdf`,
        { params: { year: reportPeriod.year, month: reportPeriod.month }, responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_${reportDialog.companyName}_${reportPeriod.year}_${String(reportPeriod.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Rapport généré');
      setReportDialog(null);
    } catch (e) {
      toast.error('Erreur rapport', { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4" data-testid="companies-admin-page">
      {/* ================== Page header ================== */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui' }}>
            Clients / Entreprises
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez vos entreprises et suivez la complétude des profils.
          </p>
        </div>
        <Button
          onClick={() => setNewCompanyOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
          data-testid="new-company-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle entreprise
        </Button>
      </div>

      {/* ================== Filters bar ================== */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Rechercher (nom, email, NCC, RCCM...)"
                className="pl-9 h-10 bg-white border-slate-200 rounded-md"
                data-testid="companies-search"
              />
            </div>
            <Button variant="outline" size="sm" className="h-10 border-slate-200" data-testid="filters-btn">
              <Filter className="w-4 h-4 mr-1.5" /> Filtres
            </Button>
            <Select
              value={filterReadiness === 'all' && filterFne === 'all' && filterTva === 'all' && filterSector === 'all' ? 'all' : 'custom'}
              onValueChange={(v) => {
                if (v === 'all') {
                  setFilterReadiness('all'); setFilterFne('all'); setFilterTva('all'); setFilterSector('all');
                }
              }}
            >
              <SelectTrigger className="w-[110px] h-10 border-slate-200" data-testid="filter-all">
                <SelectValue>{filterReadiness === 'all' && filterFne === 'all' && filterTva === 'all' && filterSector === 'all' ? 'Tous' : 'Filtré'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filterReadiness}
              onValueChange={(v) => {
                setFilterReadiness(v);
                const next = new URLSearchParams(searchParams);
                if (v === 'all') next.delete('readiness'); else next.set('readiness', v);
                setSearchParams(next, { replace: true });
              }}
            >
              <SelectTrigger className="w-[170px] h-10 border-slate-200" data-testid="filter-readiness">
                <SelectValue placeholder="Profils incomplets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les profils</SelectItem>
                <SelectItem value="incomplete">Profils incomplets</SelectItem>
                <SelectItem value="ready">Profils complets</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterFne} onValueChange={setFilterFne}>
              <SelectTrigger className="w-[150px] h-10 border-slate-200" data-testid="filter-fne">
                <SelectValue placeholder="FNE manquant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">FNE — Tous</SelectItem>
                <SelectItem value="no">FNE manquant</SelectItem>
                <SelectItem value="yes">FNE activé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTva} onValueChange={setFilterTva}>
              <SelectTrigger className="w-[140px] h-10 border-slate-200" data-testid="filter-tva">
                <SelectValue placeholder="Paiement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">TVA — Tous</SelectItem>
                {TVA_OPTIONS.slice(1).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSector} onValueChange={setFilterSector}>
              <SelectTrigger className="w-[140px] h-10 border-slate-200" data-testid="filter-sector">
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Secteur — Tous</SelectItem>
                {sectorOptions.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => {
                setSearch(''); setFilterFne('all'); setFilterTva('all'); setFilterSector('all'); setFilterReadiness('all');
                setSearchParams({ tab: 'companies' }, { replace: true });
              }}
              className="text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline ml-auto"
              data-testid="reset-filters-link"
            >
              Réinitialiser
            </button>
            <Button variant="ghost" size="sm" onClick={load} className="h-10" data-testid="companies-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={exportCsv} className="h-10" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" />
              <span className="text-xs">{selected.size > 0 ? `CSV (${selected.size})` : 'CSV'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ================== Bulk Actions Bar ================== */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex flex-wrap items-center gap-2 shadow-sm" data-testid="bulk-actions-bar">
          <CheckSquare className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-semibold text-emerald-900">{selected.size} entreprise(s) sélectionnée(s)</span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={() => runBulk('fne_enable')} data-testid="bulk-fne-enable">
              <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-600" />Activer FNE
            </Button>
            <Button size="sm" variant="outline" onClick={() => runBulk('fne_disable')} data-testid="bulk-fne-disable">
              <X className="w-3.5 h-3.5 mr-1 text-rose-600" />Désactiver FNE
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkPolicyDialog(true)} data-testid="bulk-policy">
              Modalité paiement
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkSectorDialog(true)} data-testid="bulk-sector">
              Secteur
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              // Une "company" est un group MongoDB sans `id` au top-level.
              // L'assignation se fait sur les membres clients → on aplatit tous les members clients des companies sélectionnées.
              const ids = Array.from(selected).flatMap((name) => {
                const company = filtered.find((c) => c.companyName === name);
                if (!company) return [];
                return (company.members || [])
                  .filter((m) => m.role === 'client' && m.id)
                  .map((m) => m.id);
              });
              window.__bulkClientIds__ = ids;
              setAssignCommercialClient({ id: '__BULK__', companyName: `${selected.size} entreprises sélectionnées`, assignedCommercialName: null });
              setAssignCommercialOpen(true);
            }} data-testid="bulk-commercial">
              <UsersIcon className="w-3.5 h-3.5 mr-1 text-emerald-600" />Assigner commercial
            </Button>
            <Button size="sm" variant="outline" onClick={exportCsv} data-testid="bulk-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" />Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ================== Companies Table ================== */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <p className="text-slate-500 p-6">Chargement...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60 border-b border-slate-200 hover:bg-slate-50/60">
                    <TableHead className="w-10 py-3 pl-4">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all" />
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <button
                        onClick={() => handleSort('companyName')}
                        className="inline-flex items-center gap-1 hover:text-emerald-700 transition-colors"
                        data-testid="sort-companyName"
                      >
                        Client
                        {sortBy === 'companyName' && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </button>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Complétude</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fiscalité</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paiement</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Activité</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Relation</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((c) => (
                    <TableRow
                      key={c.companyName}
                      className={`border-b border-slate-100 hover:bg-emerald-50/20 transition-colors ${selected.has(c.companyName) ? 'bg-emerald-50/40' : ''}`}
                      data-testid={`company-row-${c.companyName}`}
                    >
                      <TableCell className="py-4 pl-4 align-top">
                        <Checkbox
                          checked={selected.has(c.companyName)}
                          onCheckedChange={() => toggleOne(c.companyName)}
                          data-testid={`select-${c.companyName}`}
                        />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <ClientCell company={c} onOpen={() => openProfileDrawer(c)} />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <CompletenessBlock company={c} onClickDetails={() => openProfileDrawer(c)} />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <FiscalityCell company={c} onClickDetails={() => openProfileDrawer(c)} />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <PaymentCell company={c} />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <ActivityCell company={c} />
                      </TableCell>
                      <TableCell className="py-4 align-top">
                        <RelationCell company={c} />
                      </TableCell>
                      <TableCell className="py-4 pr-4 align-top">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openProfileDrawer(c)}
                            className="h-8 px-3 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                            data-testid={`view-${c.companyName}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
                                data-testid={`more-${c.companyName}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => openProfileDrawer(c)} data-testid={`menu-edit-${c.companyName}`}>
                                <Edit3 className="w-3.5 h-3.5 mr-2" /> Modifier l&apos;entreprise
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                // Company group has no top-level id; on assigne via le bulk endpoint sur tous les membres clients
                                const ids = (c.members || []).filter((m) => m.role === 'client' && m.id).map((m) => m.id);
                                window.__bulkClientIds__ = ids;
                                setAssignCommercialClient({
                                  id: '__BULK__',
                                  companyName: c.companyName,
                                  assignedCommercialName: c.assignedCommercialName || null,
                                });
                                setAssignCommercialOpen(true);
                              }} data-testid={`menu-commercial-${c.companyName}`}>
                                <UsersIcon className="w-3.5 h-3.5 mr-2 text-emerald-600" /> {c.assignedCommercialName ? `Commercial : ${c.assignedCommercialName}` : 'Assigner un commercial'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openStatement(c)} data-testid={`menu-statement-${c.companyName}`}>
                                <Mail className="w-3.5 h-3.5 mr-2" /> Envoyer un relevé
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setReportDialog(c)} data-testid={`menu-report-${c.companyName}`}>
                                <FileBarChart className="w-3.5 h-3.5 mr-2" /> Rapport mensuel
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => window.open(`/admin/clients?company=${encodeURIComponent(c.companyName)}&tab=users`, '_self')} data-testid={`menu-members-${c.companyName}`}>
                                <UsersIcon className="w-3.5 h-3.5 mr-2" /> Gérer les membres
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => window.open(`/admin/operations?company=${encodeURIComponent(c.companyName)}`, '_self')} data-testid={`menu-ops-${c.companyName}`}>
                                <FileText className="w-3.5 h-3.5 mr-2" /> Voir les opérations
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => window.open(`/admin/fne/invoices?company=${encodeURIComponent(c.companyName)}`, '_self')} data-testid={`menu-invoices-${c.companyName}`}>
                                <FileBarChart className="w-3.5 h-3.5 mr-2" /> Voir les factures
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setMergeDialog(c)} className="text-amber-700" data-testid={`menu-merge-${c.companyName}`}>
                                <GitMerge className="w-3.5 h-3.5 mr-2" /> Fusionner
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                          <Building2 className="w-12 h-12 text-slate-300" />
                          <p className="text-sm font-medium text-slate-500">Aucune entreprise correspondante</p>
                          <p className="text-xs text-slate-400">Essayez de modifier les filtres ou la recherche.</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSearch(''); setFilterFne('all'); setFilterTva('all'); setFilterSector('all'); setFilterReadiness('all'); setSearchParams({ tab: 'companies' }, { replace: true }); }}
                            className="mt-2"
                            data-testid="reset-filters-btn"
                          >
                            Réinitialiser les filtres
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* ================== Pagination footer ================== */}
        {!loading && filtered.length > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-600">
            <div data-testid="pagination-summary">
              Affichage <span className="font-medium text-slate-800">{(page - 1) * pageSize + 1}</span> à{' '}
              <span className="font-medium text-slate-800">{Math.min(page * pageSize, filtered.length)}</span> sur{' '}
              <span className="font-medium text-slate-800">{filtered.length}</span> entreprise{filtered.length > 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 w-8 p-0"
                data-testid="page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 7).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors ${
                    p === page
                      ? 'bg-emerald-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`page-${p}`}
                >
                  {p}
                </button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 w-8 p-0"
                data-testid="page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                <SelectTrigger className="h-8 w-[70px]" data-testid="page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>par page</span>
            </div>
          </div>
        )}
      </Card>

      {/* === Profile Drawer === */}
      <Dialog open={!!profileDrawer} onOpenChange={(o) => !o && setProfileDrawer(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-700" />
              Profil entreprise — {profileDrawer?.companyName}
            </DialogTitle>
            <DialogDescription>
              {profileDrawer?.memberCount} membre(s) · {profileDrawer?.operationsCount} opération(s)
              · Modifications propagées à tous les membres + audit OHADA
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="profile" className="mt-2">
            <TabsList>
              <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
              <TabsTrigger value="members" data-testid="tab-members">Membres ({profileDrawer?.members?.length || 0})</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit ({auditItems.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="profile" className="space-y-3 pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">NCC (Compte Contribuable)</Label>
                  <Input
                    value={profileForm.compteContribuable || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, compteContribuable: e.target.value })}
                    data-testid="profile-ncc"
                  />
                </div>
                <div>
                  <Label className="text-xs">RCCM</Label>
                  <Input
                    value={profileForm.rccm || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, rccm: e.target.value })}
                    data-testid="profile-rccm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Statut TVA</Label>
                  <Select
                    value={profileForm.tvaStatus || ''}
                    onValueChange={(v) => setProfileForm({ ...profileForm, tvaStatus: v })}
                  >
                    <SelectTrigger data-testid="profile-tva"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {TVA_OPTIONS.filter((o) => o.value).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Email facturation (FNE)</Label>
                  <Input
                    type="email"
                    value={profileForm.billingEmail || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, billingEmail: e.target.value })}
                    placeholder={profileDrawer?.billingEmailFallback ? `Fallback: ${profileDrawer.billingEmailFallback}` : 'compta@...'}
                    data-testid="profile-billing-email"
                  />
                  {profileDrawer?.billingEmailFallback && !profileForm.billingEmail && (
                    <p className="text-[10px] text-amber-700 mt-1">
                      Si vide, le système utilisera : <strong>{profileDrawer.billingEmailFallback}</strong> (premier admin/finance)
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Adresse</Label>
                  <Input
                    value={profileForm.address || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    data-testid="profile-address"
                  />
                </div>
                <div>
                  <Label className="text-xs">Secteur d&apos;activité</Label>
                  <Select
                    value={profileForm.sectorCode || ''}
                    onValueChange={(v) => setProfileForm({ ...profileForm, sectorCode: v })}
                  >
                    <SelectTrigger data-testid="profile-sector"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {sectors.map((s) => (
                        <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {profileForm.sectorCode === 'AUTRE' && (
                    <Input
                      className="mt-1"
                      placeholder="Précisez le secteur..."
                      value={profileForm.customSector || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, customSector: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Modalité de paiement par défaut</Label>
                  <Select
                    value={profileForm.defaultPaymentPolicyId || ''}
                    onValueChange={(v) => setProfileForm({ ...profileForm, defaultPaymentPolicyId: v })}
                  >
                    <SelectTrigger data-testid="profile-policy"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {policies.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    Mode de paiement préféré (FNE)
                    <span className="text-[10px] text-slate-500">— Client → Baobab</span>
                  </Label>
                  <Select
                    value={profileForm.preferredPaymentMode || ''}
                    onValueChange={(v) => setProfileForm({ ...profileForm, preferredPaymentMode: v })}
                  >
                    <SelectTrigger data-testid="profile-preferred-payment-mode"><SelectValue placeholder="Chèque (défaut)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cheque">Chèque</SelectItem>
                      <SelectItem value="virement">Virement bancaire</SelectItem>
                      <SelectItem value="cash">Espèces</SelectItem>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      <SelectItem value="card">Carte bancaire</SelectItem>
                      <SelectItem value="a_terme">À terme (différé)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Utilisé pour la certification FNE. Distinct du mode de paiement chauffeur.
                  </p>
                </div>
                <div className="md:col-span-2 flex items-center gap-2 pt-2 border-t">
                  <Checkbox
                    id="fne-enabled"
                    checked={!!profileForm.fneBillingEnabled}
                    onCheckedChange={(v) => setProfileForm({ ...profileForm, fneBillingEnabled: !!v })}
                    data-testid="profile-fne"
                  />
                  <Label htmlFor="fne-enabled" className="text-sm cursor-pointer flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Facturation FNE activée pour cette entreprise
                  </Label>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs flex items-center gap-1.5">
                    📅 Date début certification FNE (entreprise)
                  </Label>
                  <Input
                    type="date"
                    value={
                      profileForm.fneBillingStartDate
                        ? String(profileForm.fneBillingStartDate).slice(0, 10)
                        : ''
                    }
                    onChange={(e) =>
                      setProfileForm({ ...profileForm, fneBillingStartDate: e.target.value || null })
                    }
                    placeholder="Ex: 2026-03-15"
                    className="mt-1"
                    data-testid="profile-fne-start-date"
                  />
                  <p className="text-[11px] text-slate-500 italic mt-1">
                    Si renseignée, prime sur la date globale. Les opérations antérieures seront marquées
                    « Hors périmètre client ».
                  </p>
                </div>
              </div>

              {/* LOT B — Référence externe client (libellé configurable) */}
              {profileDrawer?.companyName && (
                <ExternalReferenceConfigCard companyName={profileDrawer.companyName} />
              )}
            </TabsContent>
            <TabsContent value="members" className="pt-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead className="text-center">Actif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(profileDrawer?.members || []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-xs">{m.firstName} {m.lastName}</TableCell>
                      <TableCell className="text-xs font-mono">{m.email}</TableCell>
                      <TableCell className="text-xs">{m.client_role || m.role}</TableCell>
                      <TableCell className="text-center">{m.isActive ? '✓' : '✗'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
            <TabsContent value="audit" className="pt-3 space-y-2">
              {auditItems.length === 0 ? (
                <p className="text-xs text-slate-500">Aucun changement enregistré.</p>
              ) : (
                <ul className="space-y-2 text-xs max-h-64 overflow-y-auto">
                  {auditItems.map((a) => (
                    <li key={a.id} className="border-l-2 border-emerald-400 pl-2 py-1">
                      <div className="font-semibold text-slate-800">{a.action}</div>
                      <div className="text-slate-500">{a.createdAt} · par {a.actorEmail || a.actorId || '—'}</div>
                      {a.details?.reason && <div className="italic text-slate-600">&laquo;&nbsp;{a.details.reason}&nbsp;&raquo;</div>}
                      {a.details?.fieldsChanged && (
                        <div className="text-[10px] text-emerald-700 mt-0.5">
                          Champs : {a.details.fieldsChanged.join(', ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter className="border-t pt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => { setProfileDrawer(null); openRename(profileDrawer); }} data-testid="drawer-rename">
              <Edit3 className="w-3.5 h-3.5 mr-1" />Renommer
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setProfileDrawer(null); setMergeDialog(profileDrawer); }} data-testid="drawer-merge">
              <GitMerge className="w-3.5 h-3.5 mr-1" />Fusionner
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setProfileDrawer(null)}>Annuler</Button>
            <Button onClick={submitProfile} disabled={savingProfile} data-testid="profile-save">
              {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Rename Dialog === */}
      <Dialog open={!!renameDialog} onOpenChange={(o) => !o && setRenameDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer &laquo; {renameDialog?.companyName} &raquo;</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Nouveau nom *</Label>
              <Input
                value={renameForm.newName}
                onChange={(e) => { setRenameForm({ ...renameForm, newName: e.target.value }); checkSimilar(e.target.value); }}
                data-testid="rename-newname-input"
              />
              {similar.length > 0 && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs">
                  <div className="font-semibold text-amber-900 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />Noms similaires existants :
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {similar.map((m) => (
                      <li key={m.companyName} className="text-amber-800">
                        &bull; {m.companyName} <span className="text-amber-600">({Math.round(m.similarity * 100)}%)</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 text-amber-700">Si l&apos;un de ces noms correspond, utilisez plutôt Fusionner.</div>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Raison (audit OHADA)</Label>
              <Input
                value={renameForm.reason}
                onChange={(e) => setRenameForm({ ...renameForm, reason: e.target.value })}
                placeholder="Ex : Rebranding 2026, correction faute de frappe..."
                data-testid="rename-reason-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-amber-200 bg-amber-50 -mx-6 -mb-4 px-6 py-3">
              <Checkbox
                id="retro"
                checked={renameForm.retroactive}
                onCheckedChange={(v) => setRenameForm({ ...renameForm, retroactive: !!v })}
                data-testid="rename-retroactive-checkbox"
              />
              <Label htmlFor="retro" className="text-xs cursor-pointer">
                <AlertTriangle className="w-3 h-3 inline text-amber-600" /> <strong>Rétroactif</strong> — met à jour aussi le snapshot dans les anciennes opérations, contrats, factures contractuelles, devis et bons signés ({renameDialog?.operationsCount} op(s)).
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(null)}>Annuler</Button>
            <Button onClick={submitRename} disabled={!renameForm.newName || renameForm.newName === renameDialog?.companyName} data-testid="rename-submit-btn">
              Confirmer renommage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Merge Dialog === */}
      <Dialog open={!!mergeDialog} onOpenChange={(o) => !o && setMergeDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fusionner &laquo; {mergeDialog?.companyName} &raquo; dans une autre entreprise</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Entreprise de destination *</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="— Choisir —" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {companies.filter((c) => c.companyName !== mergeDialog?.companyName).map((c) => (
                    <SelectItem key={c.companyName} value={c.companyName}>
                      {c.companyName} ({c.memberCount} m. / {c.operationsCount} ops)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-2 bg-rose-50 border border-rose-300 rounded text-xs text-rose-900">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              <strong>Action irréversible.</strong> Les {mergeDialog?.memberCount} membre(s) et {mergeDialog?.operationsCount} opération(s) seront transférés.
              Le nom &laquo; {mergeDialog?.companyName} &raquo; disparaîtra. Audit log conservé.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog(null)}>Annuler</Button>
            <Button variant="destructive" onClick={submitMerge} disabled={!mergeTarget} data-testid="merge-submit-btn">
              Confirmer fusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Bulk Payment Policy Dialog === */}
      <Dialog open={bulkPolicyDialog} onOpenChange={setBulkPolicyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modalité de paiement pour {selected.size} entreprise(s)</DialogTitle>
          </DialogHeader>
          <Select value={bulkPolicyValue} onValueChange={setBulkPolicyValue}>
            <SelectTrigger><SelectValue placeholder="— Choisir —" /></SelectTrigger>
            <SelectContent>
              {policies.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPolicyDialog(false)}>Annuler</Button>
            <Button
              onClick={() => runBulk('set_payment_policy', { defaultPaymentPolicyId: bulkPolicyValue })}
              disabled={!bulkPolicyValue}
              data-testid="bulk-policy-confirm"
            >
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Bulk Sector Dialog === */}
      <Dialog open={bulkSectorDialog} onOpenChange={setBulkSectorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Secteur d&apos;activité pour {selected.size} entreprise(s)</DialogTitle>
          </DialogHeader>
          <Select value={bulkSectorValue} onValueChange={setBulkSectorValue}>
            <SelectTrigger><SelectValue placeholder="— Choisir —" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {sectors.map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSectorDialog(false)}>Annuler</Button>
            <Button
              onClick={() => runBulk('set_sector', { sectorCode: bulkSectorValue })}
              disabled={!bulkSectorValue}
              data-testid="bulk-sector-confirm"
            >
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Report Period Dialog === */}
      <Dialog open={!!reportDialog} onOpenChange={(o) => !o && setReportDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-emerald-700" />
              Rapport mensuel — {reportDialog?.companyName}
            </DialogTitle>
            <DialogDescription>
              Choisissez la période à inclure dans le rapport PDF complet.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-xs">Année</Label>
              <Input
                type="number"
                min="2020" max="2100"
                value={reportPeriod.year}
                onChange={(e) => setReportPeriod({ ...reportPeriod, year: parseInt(e.target.value || '0', 10) })}
                data-testid="report-year"
              />
            </div>
            <div>
              <Label className="text-xs">Mois</Label>
              <Select
                value={String(reportPeriod.month)}
                onValueChange={(v) => setReportPeriod({ ...reportPeriod, month: parseInt(v, 10) })}
              >
                <SelectTrigger data-testid="report-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{String(i + 1).padStart(2, '0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialog(null)}>Annuler</Button>
            <Button onClick={downloadReport} data-testid="report-download">
              <Download className="w-3.5 h-3.5 mr-1" />Télécharger PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Statement Dialog === */}
      <ClientStatementDialog
        open={statementDialog}
        onOpenChange={setStatementDialog}
        client={statementClient}
      />

      {/* === Assign Commercial Dialog (Sprint 1) === */}
      <AssignCommercialDialog
        open={assignCommercialOpen}
        onOpenChange={setAssignCommercialOpen}
        client={assignCommercialClient}
        onSuccess={() => { setAssignCommercialOpen(false); load(); }}
      />

      {/* === New Company Info Dialog === */}
      <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-700" />
              Créer une nouvelle entreprise
            </DialogTitle>
            <DialogDescription>
              Dans Baobab Connect, une entreprise est créée à partir de son premier utilisateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Pour créer une nouvelle entreprise :</p>
            <ol className="list-decimal pl-5 space-y-1 text-slate-600">
              <li>Ouvrez l&apos;onglet <strong>Utilisateurs</strong>.</li>
              <li>Cliquez sur <em>Nouveau client</em>.</li>
              <li>Renseignez le nom de l&apos;entreprise au moment de la création.</li>
              <li>Complétez ensuite le profil (NCC, RCCM, TVA, secteur...) ici.</li>
            </ol>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCompanyOpen(false)}>Fermer</Button>
            <Button
              onClick={() => { setNewCompanyOpen(false); setSearchParams({ tab: 'users' }, { replace: true }); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="new-company-goto-users"
            >
              Aller à Utilisateurs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
